import { useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { Helmet } from 'react-helmet-async'
import { ChevronLeft, Menu } from 'lucide-react'
import { parentPathFor, titleForPath } from '@/roles/owner/nav'
import { OwnerDrawer } from '@/roles/owner/OwnerDrawer'
import { usePageEnter } from '@/shared/shell/usePageEnter'
import { useAuthStore } from '@/shared/store/authStore'
import { useLayoutStore } from '@/shared/store/layoutStore'
import { useHostBack } from '@/shared/shell/hostNavigation'
import { Button } from '@/shared/ui'

// 점주는 메뉴가 적어 Rail·하단탭 없이 헤더 + 햄버거 메뉴만 둔다 (모든 화면 폭 공통).
export default function OwnerShell() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const hostBack = useHostBack()
  const stackBack = useLayoutStore((state) => state.stackBack)
  const user = useAuthStore((state) => state.user)
  const setDrawerOpen = useLayoutStore((state) => state.setDrawerOpen)
  const mainRef = useRef<HTMLElement>(null)
  usePageEnter(mainRef, pathname)
  const stackTitle = useLayoutStore((state) => state.stackTitle)
  // 상세 화면이 useStackHeader 로 넘긴 제목(서류명 등)이 메뉴 제목보다 우선.
  const title = stackTitle ?? titleForPath(pathname) ?? '홈'
  const parentPath = parentPathFor(pathname)

  return (
    <div className="app-viewport app-safe-shell flex flex-col overflow-hidden bg-bg">
      <Helmet>
        <title>{`bizbee QSC - ${title}`}</title>
      </Helmet>
      <header
        data-qsc-chrome="true"
        className="flex h-[calc(3.5rem+var(--safe-top))] shrink-0 items-center gap-3 border-b border-border-subtle bg-surface px-4 pt-[var(--safe-top)] lg:px-7"
      >
        {(hostBack || stackBack || parentPath) && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (hostBack) hostBack()
              else if (stackBack) stackBack()
              else if (parentPath) navigate(parentPath)
            }}
            aria-label="뒤로가기"
            className="size-11 shrink-0"
          >
            <ChevronLeft aria-hidden className="size-5" />
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-preset-body-strong text-text">
            {title === '홈' ? 'bizbee QSC' : title}
          </h1>
          <p className="truncate text-preset-helper text-text-subtle">
            {user?.userNm ?? '사용자'} · Owner / 점주
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-11 shrink-0"
          onClick={() => setDrawerOpen(true)}
          aria-label="메뉴 열기"
        >
          <Menu aria-hidden className="size-5" />
        </Button>
      </header>
      <OwnerDrawer />
      <main
        ref={mainRef}
        data-qsc-scroll="content"
        // 홈은 흰 배경이라, 끝에서 튕길 때 드러나는 스크롤 영역도 같은 색으로 맞춘다.
        className={`min-h-0 flex-1 overflow-auto ${pathname.endsWith('/home') ? 'bg-surface' : 'bg-bg'}`}
      >
        <Outlet />
      </main>
    </div>
  )
}
